import { useMemo, useState } from "react";
import {
  TrendingUp,
  Smartphone,
  Filter,
  LayoutGrid,
  Monitor,
  ShoppingCart,
  ArrowUpAZ,
  Layers,
} from "lucide-react";
import useScroll from "../../hooks/useScroll";
import { templates } from "../../utilities/objects/template";
import ResponsiveImage from "../common/ResponsiveImage";


const categories = [
  { id: "all", name: "Tất cả", icon: Layers },
  { id: "web-commerce", name: "Website Doanh nghiệp & Thương mại điện tử", icon: ShoppingCart },
  { id: "dashboard", name: "Giao diện Hệ thống quản trị", icon: Monitor },
  { id: "landing", name: "Landing Page tối ưu chiến dịch Marketing", icon: TrendingUp },
  { id: "mobile-ui", name: "Ứng dụng di động (Mobile UI UX)", icon: Smartphone },
];

const sortOptions = [
  { value: "newest", label: "Mới nhất" },
  { value: "name-asc", label: "Tên A-Z" },
  { value: "name-desc", label: "Tên Z-A" },
];

export default function SectionTemplate() {
  const { scrollRef, handleMouseEnter, handleMouseLeave } = useScroll();
  const [sortBy, setSortBy] = useState("newest");
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const pageSize = 12;

  const filteredTemplates = useMemo(() => {
    let data = templates.filter((tpl) => {
      const matchCategory = category === "all" || tpl.solutionType === category;
      const keyword = searchTerm.trim().toLowerCase();
      const searchable = `${tpl.name} ${tpl.title ?? ""} ${tpl.industry ?? ""}`.toLowerCase();
      return searchable.includes(keyword) && matchCategory;
    });

    if (sortBy === "name-asc") {
      data = [...data].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "name-desc") {
      data = [...data].sort((a, b) => b.name.localeCompare(a.name));
    } else {
      data = [...data].sort((a, b) => b.id - a.id);
    }

    return data;
  }, [category, searchTerm, sortBy]);

  const pagedTemplates = filteredTemplates.slice((page - 1) * pageSize, page * pageSize);

  const handleCategoryChange = (value: string) => {
    setCategory(value);
    setPage(1);
  };

  return (
    <div className="w-full bg-[#fffaf9]">
      <div className="container mx-auto px-4 py-6 lg:px-6">
        <div className="mb-6 rounded-[24px] border border-red-100 bg-white p-5 shadow-[0_14px_34px_rgba(127,29,29,0.05)] sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="hidden items-center rounded-lg bg-red-50 px-4 py-2 text-sm text-slate-600 lg:flex">
              <strong className="mx-1 text-red-600">{pagedTemplates.length}</strong>/
              <strong className="mx-1 text-red-600">{filteredTemplates.length}</strong> nền tảng
            </div>

            <div className="relative w-full lg:w-[170px]">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
                className="w-full h-10 px-4 pr-10 rounded-lg border border-gray-300 bg-white text-slate-700 text-sm font-medium focus:outline-none appearance-none cursor-pointer"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                <ArrowUpAZ size={18} />
              </div>
            </div>

            <div className="lg:flex-1">
              <div className="relative flex shadow-sm rounded-lg border border-gray-300 bg-white overflow-hidden">
                <input
                  type="text"
                  placeholder="Tìm theo tên giải pháp, ngành hoặc nền tảng..."
                  className="w-full h-10 px-4 text-slate-700 placeholder-gray-400 focus:outline-none text-sm"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setPage(1);
                    }}
                    className="px-2 text-gray-400 hover:text-gray-600 focus:outline-none text-sm"
                    type="button"
                  >
                    ✕
                  </button>
                )}
                <button
                  className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold h-10 px-6 transition flex items-center justify-center"
                  type="button"
                >
                  Tìm
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-100 px-4 py-3 text-sm font-medium text-slate-600 lg:hidden"
            >
              <Filter size={18} />
              Bộ lọc
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row">
          <aside className={`lg:w-[300px] ${showMobileFilters ? "block" : "hidden lg:block"}`}>
            <div className="sticky top-24 rounded-[24px] border border-red-100 bg-white p-5 shadow-[0_14px_34px_rgba(127,29,29,0.05)]">
              <div className="mb-4 flex items-center gap-2 border-b border-red-100 pb-3">
                <LayoutGrid size={20} className="text-red-600" />
                <h3 className="font-semibold text-[#2f1a1a]">Mục tiêu giải pháp</h3>
              </div>

              <div className="space-y-2">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.id)}
                      className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                        isActive
                          ? "bg-red-50 font-semibold text-red-700"
                          : "text-slate-600 hover:bg-[#fff8f8] hover:text-red-700"
                      }`}
                    >
                      <Icon
                        size={20}
                        
                        className={isActive ? "text-red-600" : "text-slate-400"}
                      />
                      <span className="text-sm leading-6">{cat.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <div className="flex-1">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {pagedTemplates.map((tpl) => (
                <article
                  key={tpl.id}
                  className="overflow-hidden rounded-[24px] border border-red-100 bg-white shadow-[0_14px_34px_rgba(127,29,29,0.05)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_44px_rgba(127,29,29,0.08)]"
                >
                  <div
                    className="hide-scrollbar relative h-[230px] overflow-y-auto overflow-x-hidden"
                    ref={(el) => {
                      scrollRef.current[tpl.id] = el;
                    }}
                    onMouseEnter={() => handleMouseEnter(tpl.id)}
                    onMouseLeave={() => handleMouseLeave(tpl.id)}
                  >
                    <ResponsiveImage src={tpl.img} alt={tpl.name} className="w-full select-none" />
                  </div>

                  <div className="p-5">
                    <span className="inline-flex rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-[11px] font-medium text-red-700">
                      {tpl.industry}
                    </span>

                    <div className="mt-3 text-[17px] font-semibold leading-7 text-[#2f1a1a]">
                      {tpl.title}
                    </div>

                    <ul className="mt-4 space-y-2 text-left">
                      {tpl.highlights?.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-[13px] leading-6 text-slate-600">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>

            {filteredTemplates.length > pageSize && (
              <div className="mt-8 flex justify-center items-center gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition"
                >
                  Trước
                </button>
                {Array.from({ length: Math.ceil(filteredTemplates.length / pageSize) }).map((_, idx) => {
                  const p = idx + 1;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-9 h-9 rounded-lg border text-sm font-medium transition ${
                        page === p
                          ? "bg-red-600 border-red-600 text-white"
                          : "border-gray-200 text-gray-600 bg-white hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  disabled={page === Math.ceil(filteredTemplates.length / pageSize)}
                  onClick={() => setPage(page + 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition"
                >
                  Sau
                </button>
              </div>
            )}

            {pagedTemplates.length === 0 && (
              <div className="py-16 text-center">
                <div className="mb-4 text-6xl text-slate-300">⌕</div>
                <h3 className="mb-2 text-xl font-semibold text-slate-700">Không tìm thấy kết quả</h3>
                <p className="text-slate-500">Hãy thử đổi nhóm giải pháp hoặc từ khóa tìm kiếm.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
